﻿import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { getAppStatusConfig } from '../../../utils.ts';
import type { TApiApplication } from '../../../types.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { APP_STATUS_OPTIONS } from '~/modules/covenants/constants.tsx';
import { StatusFilterValue } from '~/modules/covenants/types.ts';
import { StatusChipWithTooltip } from '~/modules/covenants/components/StatusChipWithTooltip.tsx';
import { getApplicationStatusChipSx } from '~/modules/covenants/tableUtils.ts';

interface IStatusFilterHeaderProps {
  label?: string;
  filter: StatusFilterValue;
  onSelect: (value: StatusFilterValue) => void;
}

export const StatusFilterHeader: React.FC<IStatusFilterHeaderProps> = ({
  label = 'Статус',
  filter,
  onSelect,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentLabel = APP_STATUS_OPTIONS.find((o) => o.value === filter)?.label;

  const handleSelect = (value: StatusFilterValue) => {
    onSelect(value);
    setAnchorEl(null);
  };

  const tooltipTitle =
    filter === StatusFilterValue.All ? 'Фільтр за статусом' : `Фільтр: ${currentLabel}`;

  return (
    <Box sx={dataFormStyles.headerInline}>
      {label}
      <Tooltip title={tooltipTitle}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={dataFormStyles.headerControlButton(filter !== StatusFilterValue.All)}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: dataFormStyles.filterMenuPaper,
          },
        }}
      >
        {APP_STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={filter === opt.value}
            onClick={() => handleSelect(opt.value)}
            sx={dataFormStyles.filterMenuItem}
          >
            {opt.value === StatusFilterValue.All ? (
              <Typography variant="body2">{opt.label}</Typography>
            ) : (
              <StatusChip status={opt.value as TApiApplication['status']} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const StatusChip: React.FC<{ status: TApiApplication['status'] }> = ({ status }) => {
  const { label, color, description } = getAppStatusConfig(status);

  return (
    <StatusChipWithTooltip
      label={label}
      color={color}
      description={description}
      chipSx={getApplicationStatusChipSx(status)}
    />
  );
};
